import { sortBy } from 'lodash-es';

import type { JSONString } from '@fragment/types/json';
import { JSONStringSchema } from '@fragment/types/json';

import { stringify as bigIntStringify } from './bigints';

/**
 * A bigint-compatible wrapper over JSON.stringify() that returns an opaque type.
 * Warning: this function is not invertible with JSON.parse(), because it
 * converts bigints to strings (but only some of them).
 * */
export const stringify = (obj: unknown, spacing?: number) => {
  // Don't need to check if it's a valid JSON string, because JSON.stringify will
  // throw an error if it's not.
  return bigIntStringify(obj, spacing);
};

export function isJSONString(s: string): s is JSONString {
  const res = JSONStringSchema.safeParse(s);
  return res.success;
}

/**
 * Serializes an object to JSON with its top-level keys in sorted order.
 *
 * Nested objects keep their own key order, so this is only a stable identity
 * for flat objects.
 */
export const orderedStringifyAsObject = (obj: Record<string, unknown>) =>
  JSON.stringify(Object.fromEntries(Object.entries(obj).sort())) as JSONString;

/**
 * Serializes an array of objects to a JSON string in sorted order, using
 * the `sortKey` provided.
 */
export const orderedStringifyAsArray = <T extends Record<string, any>>(
  obj: T[],
  sortKey: keyof T
) => JSON.stringify(sortBy(obj, sortKey)) as JSONString;
